/* eslint-disable @typescript-eslint/no-explicit-any */
// [cleanup 2025-11-18] Unused exports removed: debugTokens, apiPut, modDeletePost, modDeleteComment, SavLike namespace
// setRefreshToken made internal (not exported)

/**
 * ✅ 401 geldiğinde otomatik refresh edip isteği tekrar dener.
 * - Access token yalnızca bellek + localStorage’da (opsiyonel).
 * - Refresh token HTTP-only cookie’dedir (backend /api/auth altında set/yenilenir).
 * - Tüm isteklerde credentials: "include" → cookie gönderilir.
 */

/**
 * Bazı editörlerde (veya yanlış tsconfig pick-up’ında) `import.meta`
 * "yalnızca module es2020/es2022/esnext..." uyarısı çıkarabiliyor.
 * Bu satırdaki `@ts-ignore` o yanlış pozitif hatayı susturur.
 * Yine de varsa window.__API_URL__ → yoksa localhost:5003'e düşer.
 */
// @ts-ignore - TS server yanlış projeyi (module=commonjs) seçerse import.meta'ya itiraz ediyor.
const VITE_API_URL = (typeof import.meta !== "undefined"
  ? ((import.meta as any)?.env?.VITE_API_URL ||
     (import.meta as any)?.env?.VITE_TEXE_API_BASE ||
     (import.meta as any)?.env?.VITE_API_BASE)
  : undefined) as string | undefined;

declare global {
  interface Window {
    __API_URL__?: string;
  }
}

const API =
  VITE_API_URL ??
  (typeof window !== "undefined" ? window.__API_URL__ : undefined) ??
  (typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? "https://api.savnot.com"
    : "http://localhost:5003");

/** Bellekte tutulan access token (sayfa yenilenince localStorage'dan okunur) */
let accessToken: string | null = (() => {
  try {
    return localStorage.getItem("accessToken");
  } catch {
    return null;
  }
})();

/** Mevcut access token'ı al (geri uyumluluk için) */
export function getToken() {
  return accessToken;
}

/** Refresh token'ı localStorage'da sakla/al (cross-origin cookie sorunu için) */
function getRefreshToken(): string | null {
  try {
    return localStorage.getItem("refreshToken");
  } catch {
    return null;
  }
}

// ⚠️ Internal function - not exported
function setRefreshToken(token: string) {
  try {
    localStorage.setItem("refreshToken", token);
  } catch {
    // storage erişimi yoksa sessiz geç
  }
}

function clearRefreshToken() {
  try {
    localStorage.removeItem("refreshToken");
  } catch {
    // storage erişimi yoksa sessiz geç
  }
}

/** Access token'ı ayarla (login/register/refresh sonrası) */
export function setToken(token: string) {
  accessToken = token;
  try {
    localStorage.setItem("accessToken", token);
  } catch {
    // sessiz geç
  }
}

/** Access token'ı temizle (logout veya refresh başarısızsa) */
export function clearToken() {
  accessToken = null;
  try {
    localStorage.removeItem("accessToken");
    clearRefreshToken(); // refresh token'ı da temizle
  } catch {
    // sessiz geç
  }
}

/** 401 flood'u engellemek için tek seferde refresh kuyruğu */
let isRefreshing = false;
let refreshWaiters: Array<(ok: boolean) => void> = [];

/** 401'de çağrılır: refresh token ile yeni access token alır */
export async function refreshAccess(): Promise<string> {
  if (isRefreshing) {
    // Devam eden bir refresh varsa bekle
    await new Promise<void>((resolve, reject) => {
      refreshWaiters.push((ok) => (ok ? resolve() : reject(new Error("refresh_failed"))));
    });
    if (!accessToken) throw new Error("refresh_failed");
    return accessToken;
  }

  isRefreshing = true;
  try {
    const refreshToken = getRefreshToken();
    const body = refreshToken ? JSON.stringify({ refresh: refreshToken }) : undefined;
    
    const r = await fetch(`${API}/api/auth/refresh`, {
      method: "POST",
      headers: body ? { "Content-Type": "application/json" } : {},
      body,
      credentials: "include",
    });

    if (!r.ok) {
      clearToken();
      refreshWaiters.splice(0).forEach((fn) => fn(false));
      throw new Error("refresh_failed");
    }

    const data = await safeJson(r);
    const token = data?.access as string | undefined;
    const newRefreshToken = data?.refresh as string | undefined;

    if (!token) {
      clearToken();
      refreshWaiters.splice(0).forEach((fn) => fn(false));
      throw new Error("refresh_missing");
    }

    setToken(token);
    if (newRefreshToken) setRefreshToken(newRefreshToken);
    refreshWaiters.splice(0).forEach((fn) => fn(true));
    return token;
  } finally {
    isRefreshing = false;
  }
}

/** Güvenli JSON parse (boş gövde veya text dönebilir) */
async function safeJson(r: Response): Promise<any> {
  const text = await r.text().catch(() => "");
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

/** İç yardımcı: Authorization header’ını ekle; 401'de özel hata fırlat (JSON istekler) */
async function runWithAuth<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string>),
  };

  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  const r = await fetch(`${API}${path}`, {
    ...init,
    cache: init?.cache ?? "no-store",
    headers,
    credentials: "include", // refresh cookie için gerekli
  });

  if (r.status === 401) throw new Error("unauthorized");

  if (!r.ok) {
    const body = await safeJson(r);
    const message =
      (typeof body?.error === "string" && body.error) ||
      (typeof body?.message === "string" && body.message) ||
      (typeof body?.detail === "string" && body.detail) ||
      (typeof body?.code === "string" && body.code) ||
      (typeof body?.raw === "string" && body.raw) ||
      "api_error";
    const error: any = new Error(message);
    error.status = r.status;
    throw error;
  }

  if (r.status === 204) return undefined as unknown as T;

  const data = await safeJson(r);
  return data as T;
}

/** İç yardımcı (UPLOAD): Authorization ekle, Content-Type elle ayarlama! */
async function runWithAuthUpload<T>(path: string, formData: FormData, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    ...(init?.headers as Record<string, string>),
  };
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  const r = await fetch(`${API}${path}`, {
    method: init?.method ?? "POST",
    body: formData,
    headers, // ❗ boundary’yi fetch ayarlar, Content-Type ekleme
    credentials: "include",
    ...init,
  });

  if (r.status === 401) throw new Error("unauthorized");

  if (!r.ok) {
    const body = await safeJson(r);
    const message =
      (typeof body?.error === "string" && body.error) ||
      (typeof body?.message === "string" && body.message) ||
      (typeof body?.detail === "string" && body.detail) ||
      (typeof body?.code === "string" && body.code) ||
      (typeof body?.raw === "string" && body.raw) ||
      "api_error";
    const error: any = new Error(message);
    error.status = r.status;
    throw error;
  }

  if (r.status === 204) return undefined as unknown as T;

  const data = await safeJson(r);
  return data as T;
}

/** 401 geldiğinde bir kez refresh dener ve isteği tekrarlar */
async function withAutoRefresh<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (e: any) {
    if (e?.message === "unauthorized") {
      await refreshAccess();
      return await fn();
    }
    throw e;
  }
}

// ────────────────────────────────────────────────────────────
// Public helpers
// ────────────────────────────────────────────────────────────

export async function apiGet<T>(path: string): Promise<T> {
  return withAutoRefresh<T>(() => runWithAuth<T>(path));
}

export async function apiPost<T>(path: string, body?: any): Promise<T> {
  const result = await withAutoRefresh<T>(() =>
    runWithAuth<T>(path, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  );

  // Centralized auth handling for login/otp flows regardless of caller
  try {
    if (typeof path === "string" && (path.includes("/api/auth/login") || path.includes("/api/auth/verify-otp"))) {
      const anyRes: any = result as any;
      const acc = anyRes?.access || anyRes?.token || anyRes?.accessToken;
      const ref = anyRes?.refresh || anyRes?.refreshToken;
      if (acc) {
        setToken(acc);
      }
      if (ref) {
        setRefreshToken(ref);
      }
    }
  } catch {
    // ignore
  }

  return result;
}

export async function apiPatch<T>(path: string, body?: any): Promise<T> {
  return withAutoRefresh<T>(() =>
    runWithAuth<T>(path, {
      method: "PATCH",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  );
}

export async function apiDelete<T>(path: string): Promise<T> {
  return withAutoRefresh<T>(() =>
    runWithAuth<T>(path, {
      method: "DELETE",
    })
  );
}

/** 🔼 FormData upload için (avatar, medya vs.) */
export async function apiUpload<T = any>(path: string, formData: FormData, init?: RequestInit): Promise<T> {
  return withAutoRefresh<T>(() => runWithAuthUpload<T>(path, formData, init));
}

/** Manuel base URL'e ihtiyaç olursa dışarı verelim */
export const API_BASE_URL = API;

// ────────────────────────────────────────────────────────────
// Notifications API (backend /api/notifications)
// ────────────────────────────────────────────────────────────

export type NotifType = "like" | "comment" | "follow" | "comment_like" | "mention" | "announcement" | "savwar_start" | "savwar_result" | "savlike_winner" | "kabile_invite" | "kabile_join_request";

export interface NotifActor {
  id: string;
  username: string;
  avatarUrl?: string | null;
  avatarUrlSmall?: string | null;
}

export interface NotifPostRef {
  id: string;
  text?: string | null;
}

export interface NotifCommentRef {
  id: string;
  text?: string | null;
  postId?: string | null; // derin link için
}

export interface NotificationItem {
  id: string;
  type: NotifType;
  read: boolean;
  createdAt: string; // ISO
  text?: string | null; // For announcement, savwar_start, savlike_winner, kabile_invite (kabile name)
  actor: NotifActor | null;
  post: NotifPostRef | null;
  comment: NotifCommentRef | null;
  kabile?: { id: string; name: string } | null; // For kabile_invite
}

export interface NotificationListResp {
  ok: true;
  items: NotificationItem[];
  nextAfter: string | null;
}

function buildQuery(params?: Record<string, string | number | boolean | undefined>) {
  const q = new URLSearchParams();
  if (!params) return "";
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** Bildirimleri getir (paginate) */
export async function fetchNotifications(params?: {
  limit?: number;
  unread?: boolean;
  after?: string; // cursor/ISO/string
}): Promise<NotificationListResp> {
  const qs = buildQuery({
    limit: params?.limit,
    unread: params?.unread ? 1 : undefined,
    after: params?.after,
  });
  return apiGet<NotificationListResp>(`/api/notifications${qs}`);
}

/** Tek bildirimi okundu işaretle */
export async function markNotificationRead(id: string): Promise<{ ok: true }> {
  return apiPost<{ ok: true }>(`/api/notifications/${id}/read`);
}

/** Tüm okunmamışları okundu işaretle */
export async function markAllNotificationsRead(): Promise<{ ok: true }> {
  return apiPost<{ ok: true }>(`/api/notifications/read-all`);
}

// ────────────────────────────────────────────────────────────
// Comments namespace  (import { Comments } from "@/lib/api")
// ────────────────────────────────────────────────────────────
export type ApiComment = {
  id: string;
  postId: string;
  author: string;
  text: string;
  createdAt?: string;
  updatedAt?: string;
};

export const Comments = {
  listComments(postId: string) {
    return apiGet<{ ok: boolean; items: ApiComment[] }>(
      `/api/comments?postId=${encodeURIComponent(postId)}`
    );
  },
  addComment(postId: string, text: string) {
    return apiPost<{ ok: boolean; comment: ApiComment }>(`/api/comments`, {
      postId,
      text,
    });
  },
  editComment(id: string, text: string) {
    return apiPatch<{ ok: boolean; comment: ApiComment }>(
      `/api/comments/${encodeURIComponent(id)}`,
      { text }
    );
  },
  deleteComment(id: string) {
    return apiDelete<{ ok: boolean }>(`/api/comments/${encodeURIComponent(id)}`);
  },
};

// ────────────────────────────────────────────────────────────
// Profile namespace  (import { Profile } from "@/lib/api")
// ────────────────────────────────────────────────────────────
export type ProfileUser = {
  id: string;
  username: string;
  email: string;
  bio?: string | null;
  favoriteQuote?: string | null;
  website?: string | null;
  preferredLanguage?: string | null;
  avatarUrl?: string | null;
  avatarUrlSmall?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export const Profile = {
  getProfile() {
    return apiGet<{ ok: boolean; user: ProfileUser }>("/api/profile/me");
  },
  patchProfile(data: {
    bio?: string | null;
    favoriteQuote?: string | null;
    website?: string | null;
    preferredLanguage?: string | null;
  }) {
    return apiPatch<{ ok: boolean; user: ProfileUser }>("/api/profile/me", data);
  },
  uploadAvatar(file: File) {
    const fd = new FormData();
    fd.append("avatar", file);
    return apiUpload<{ ok: boolean; user: ProfileUser; avatarUrl?: string; avatarUrlSmall?: string }>(
      "/api/profile/avatar",
      fd
    );
  },
  deleteAvatar() {
    return apiDelete<{ ok: boolean; user: ProfileUser }>("/api/profile/avatar");
  },
};

/* ────────────────────────────────────────────────────────────
   🔴 Realtime (SSE)
───────────────────────────────────────────────────────────── */

export type RealtimeEventPayload =
  | { type: "notification"; userId?: string; payload: any }
  | { type: "feed:new_post"; payload: any }
  | { type: "feed:delete_post"; payload: { postId: string } }
  | { type: "comment:new"; postId: string; comment: any }
  | { type: "comment:edit"; postId: string; comment: any }
  | { type: "comment:delete"; postId: string; commentId: string }
  | { type: "like:new"; payload: any };

export type RealtimeCallbacks = {
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (err: unknown) => void;

  onPing?: (t?: number) => void;

  onNotification?: (payload: any) => void;
  onFeedNewPost?: (payload: any) => void;
  onFeedDeletePost?: (payload: { postId: string }) => void;

  onRaw?: (evt: { event: string; data: any }) => void;
};

export type RealtimeConnection = {
  close: () => void;
  isClosed: () => boolean;
};

/**
 * openRealtimeStream:
 * - Authorization header + URL'de ?access=token (SSE uyumu)
 * - Otomatik yeniden bağlanma (exponential backoff)
 * - "ping/hello" özel eventleri
 * - "message" içeriğini JSON parse edip .type’a göre dispatch
 */
export function openRealtimeStream(
  callbacks: RealtimeCallbacks,
  options?: { path?: string; maxBackoffMs?: number }
): RealtimeConnection {
  const path = options?.path ?? "/api/realtime/stream";
  const maxBackoff = Math.max(2000, options?.maxBackoffMs ?? 10000);

  let aborted = false;
  let retryDelay = 1000;
  let controller: AbortController | null = null;

  const connect = async () => {
    if (aborted) return;

    // İlk bağlantıda token hazır değilse refresh dene (oturum varsa)
    if (!getToken()) {
      try {
        await refreshAccess();
      } catch {
        // misafir oturum olabilir; public stream varsa bağlanır
      }
    }

    controller = new AbortController();

    // ⬇️ URL’i token’la oluştur (SSE header taşıyamayabilir)
    const u = new URL(`${API}${path}`);
    const t = getToken();
    if (t) u.searchParams.set("access", t);

    const hdrs: Record<string, string> = {
      Accept: "text/event-stream",
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    };
    if (t) hdrs["Authorization"] = `Bearer ${t}`; // header + query birlikte zararsız, faydalı

    let res: Response | null = null;
    try {
      res = await fetch(u.toString(), {
        method: "GET",
        headers: hdrs,
        credentials: "include",
        signal: controller.signal,
      });

      if (res.status === 401) {
        await refreshAccess();
        if (aborted) return;
        controller.abort();
        controller = null;
        setTimeout(connect, 0);
        return;
      }

      if (!res.ok || !res.body) {
        throw new Error(`sse_http_${res.status}`);
      }

      retryDelay = 1000;
      callbacks.onOpen?.();

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // Her tamamlanmış SSE event bloğunu işle
      const dispatch = (chunk: string) => {
        const normalized = chunk.replace(/\r\n/g, "\n");

        let event = "message";
        const dataLines: string[] = [];

        const lines = normalized.split("\n");
        for (const raw of lines) {
          const line = raw;
          if (!line || line === "") continue; // boş satır
          if (line.startsWith(":")) continue; // yorum satırı
          if (line.startsWith("event:")) {
            event = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            dataLines.push(line.slice(5)); // baştaki boşluğu koru
          } else {
            // id:, retry: vs. şimdilik yoksayıyoruz
          }
        }

        const dataStr = dataLines.join("\n");
        let data: any = dataStr;
        try {
          data = dataStr ? JSON.parse(dataStr) : {};
        } catch {
          // düz metin olabilir — raw string olarak geç
        }

        // Ham olay
        callbacks.onRaw?.({ event, data });

        // Özel ping/hello
        if (event === "ping") {
          callbacks.onPing?.(typeof data?.t === "number" ? data.t : undefined);
          return;
        }
        if (event === "hello") return;

        // message → tip bazlı dispatch
        if (event === "message" && data && typeof data === "object") {
          const payload = data as RealtimeEventPayload;
          switch (payload.type) {
            case "notification":
              callbacks.onNotification?.((payload as any).payload);
              break;
            case "feed:new_post":
              callbacks.onFeedNewPost?.((payload as any).payload);
              break;
            case "feed:delete_post":
              callbacks.onFeedDeletePost?.((payload as any).payload);
              break;
            default:
              // comment:* ve diğerleri RealtimeProvider.onRaw içinde ele alınabilir
              break;
          }
        }
      };

      while (!aborted) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");

        // Çift newline (tamamlanmış SSE bloğu)
        let idx: number;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const chunk = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          if (chunk) dispatch(chunk);
        }
      }
    } catch (err) {
      if (!aborted) {
        callbacks.onError?.(err);
      }
    } finally {
      controller?.abort();
      controller = null;

      if (!aborted) {
        callbacks.onClose?.();
        const delay = retryDelay;
        retryDelay = Math.min(retryDelay * 2, maxBackoff);
        setTimeout(connect, delay);
      }
    }
  };

  connect();

  return {
    close() {
      aborted = true;
      controller?.abort();
    },
    isClosed() {
      return aborted;
    },
  };
}

/* ────────────────────────────────────────────────────────────
   🌐 Translate API helper
───────────────────────────────────────────────────────────── */

export async function apiTranslate(text: string, target: string, source?: string): Promise<string> {
  const r = await apiPost<{ ok: true; translated: string }>(`/api/translate`, {
    text,
    target,
    source,
  });
  return r.translated;
}

/* ────────────────────────────────────────────────────────────
   👤 Account helpers (auth/me)
───────────────────────────────────────────────────────────── */

export async function changeUsername(newUsername: string): Promise<{
  ok: true;
  access?: string;
  user: { id: string; username: string; email: string };
}> {
  return apiPatch(`/api/auth/me/username`, { newUsername });
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<{
  ok: true;
  access?: string;
}> {
  return apiPatch(`/api/auth/me/password`, { currentPassword, newPassword });
}

export async function deleteMyAccount(): Promise<{ ok: true; deleted: true }> {
  return apiDelete(`/api/auth/me`);
}

/* ────────────────────────────────────────────────────────────
   🛡️ Admin & Moderation API helpers
───────────────────────────────────────────────────────────── */

export async function adminSetRole(userId: string, opts: { add?: string; remove?: string }) {
  return apiPost<{ ok: true; user: any }>(`/api/admin/users/${encodeURIComponent(userId)}/role`, opts);
}

export async function adminSuspend(userId: string, untilISO?: string, reason?: string) {
  return apiPost<{ ok: true; user: any }>(`/api/admin/users/${encodeURIComponent(userId)}/suspend`, {
    until: untilISO,
    reason,
  });
}

export async function adminShadowban(userId: string, value: boolean, reason?: string) {
  return apiPost<{ ok: true; user: any }>(`/api/admin/users/${encodeURIComponent(userId)}/shadowban`, {
    value,
    reason,
  });
}

export async function adminExemptDaily(userId: string, value: boolean) {
  return apiPost<{ ok: true; user: any }>(`/api/admin/users/${encodeURIComponent(userId)}/exempt-daily`, {
    value,
  });
}

export async function adminBanUser(userId: string, reason?: string) {
  return apiPost<{ ok: true; user: any }>(`/api/admin/users/${encodeURIComponent(userId)}/ban`, {
    reason,
  });
}

export async function adminUnbanUser(userId: string) {
  return apiDelete<{ ok: true; user: any }>(`/api/admin/users/${encodeURIComponent(userId)}/ban`);
}

export async function adminDeleteUser(userId: string) {
  return apiPost<{ ok: true; user: any }>(`/api/admin/users/${encodeURIComponent(userId)}/delete`, {});
}

export async function adminRestoreUser(userId: string) {
  return apiPost<{ ok: true; user: any }>(`/api/admin/users/${encodeURIComponent(userId)}/restore`, {});
}

// ────────────────────────────────────────────────────────────
// 🛡️ Admin Security Functions
// ────────────────────────────────────────────────────────────

export async function adminSecurityOverview() {
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/security/overview`, { method: "GET" })
  );
}

export async function adminSecurityAuditStats(range?: "24h" | "7d") {
  const query = range ? `?range=${range}` : "";
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/security/audit-stats${query}`, { method: "GET" })
  );
}

export async function adminSecuritySuspiciousIps(params?: { minScore?: number; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.minScore !== undefined) query.append("minScore", String(params.minScore));
  if (params?.limit !== undefined) query.append("limit", String(params.limit));
  if (params?.offset !== undefined) query.append("offset", String(params.offset));
  const queryString = query.toString() ? `?${query.toString()}` : "";
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/security/suspicious-ips${queryString}`, { method: "GET" })
  );
}

export async function adminSecurityRateLimitStats() {
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/security/rate-limit-stats`, { method: "GET" })
  );
}

export async function adminSecurityMetrics() {
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/security/metrics`, { method: "GET" })
  );
}

export async function adminEditPost(postId: string, payload: { text?: string }) {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: true; post: any }>(`/api/admin/posts/${encodeURIComponent(postId)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    })
  );
}

export async function adminDeletePost(postId: string) {
  return apiDelete<{ ok: true }>(`/api/admin/posts/${encodeURIComponent(postId)}`);
}

export async function adminEditComment(commentId: string, payload: { text?: string }) {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: true; comment: any }>(`/api/admin/comments/${encodeURIComponent(commentId)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    })
  );
}

export async function adminDeleteComment(commentId: string) {
  return apiDelete<{ ok: true }>(`/api/admin/comments/${encodeURIComponent(commentId)}`);
}

/* ────────────────────────────────────────────────────────────
   🗂️ Admin Audit helper
───────────────────────────────────────────────────────────── */

export type AuditRow = {
  id: string;
  action: string;
  target: string | null;
  actorId: string | null;
  actorUsername: string | null;
  createdAt: string;
  payload?: Record<string, unknown> | null;
};

export type AuditListResp = {
  ok: true;
  count: number;
  rows: AuditRow[];
};

export async function adminListAudit(params?: {
  action?: string;
  actor?: string;   // id veya username
  target?: string;  // prefix (post:, user:, comment:)
  from?: string;    // ISO
  to?: string;      // ISO
  before?: string;  // ISO (cursor)
  limit?: number;   // 1..200
}): Promise<AuditListResp> {
  const qs = buildQuery(params as any);
  return apiGet<AuditListResp>(`/api/admin/audit${qs}`);
}

/* ────────────────────────────────────────────────────────────
   🗂️ Admin user search
───────────────────────────────────────────────────────────── */

export type AdminUserBrief = {
  id: string;
  username: string;
  email: string;
  roles: string[];
  isAdmin: boolean;
  shadowBanned: boolean;
  exemptDailyLimit: boolean;   // önemli: AdminUsers.tsx bekliyor
  suspendedUntil: string | null;
  createdAt: string; // ISO
};

export type AdminUserSearchResp = {
  ok: true;
  count: number;
  items: AdminUserBrief[];
  nextCursor?: string | null;  // pagination cursor (daha eski kayıtlar)
  nextBefore?: string | null;  // (opsiyonel geriye dönük uyum)
};

export async function adminSearchUsers(params: {
  q?: string;
  limit?: number;
  cursor?: string | null;  // ⬅️ null kabul eder
  before?: string | null;  // ⬅️ null kabul eder (alias)
}): Promise<AdminUserSearchResp> {
  const qs = new URLSearchParams();
  // Only add q if it's not empty
  if (params.q && params.q.trim()) {
    qs.set("q", params.q.trim());
  }
  if (params.limit) qs.set("limit", String(params.limit));
  const cursorLike = params.cursor ?? params.before ?? undefined;
  if (cursorLike) qs.set("before", cursorLike);
  return apiGet<AdminUserSearchResp>(`/api/admin/users/search?${qs.toString()}`);
}

// Admin dashboard stats helper
export async function adminDashboardStats(): Promise<any> {
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/dashboard/stats`, { method: "GET" })
  );
}

// Admin reports helper
export async function adminGetReports(params?: {
  status?: "open" | "reviewed";
  targetType?: "user" | "post" | "comment";
}): Promise<{ ok: boolean; reports: any[] }> {
  const qs = buildQuery(params as any);
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; reports: any[] }>(
      `/api/admin/reports${qs}`,
      { method: "GET" }
    )
  );
}

export async function adminReviewReport(reportId: string): Promise<{ ok: boolean }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean }>(
      `/api/admin/reports/${reportId}/review`,
      { method: "POST" }
    )
  );
}

// Admin users with phone helper
export async function adminGetUsersWithPhone(): Promise<{ ok: boolean; users: any[] }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; users: any[] }>(
      `/api/admin/users-with-phone`,
      { method: "GET" }
    )
  );
}

/* ────────────────────────────────────────────────────────────
   💎 Admin Premium Management
───────────────────────────────────────────────────────────── */

// Premium Frames
export async function adminListPremiumFrames(): Promise<{ ok: boolean; frames: any[] }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; frames: any[] }>(
      `/api/admin/premium/frames`,
      { method: "GET" }
    )
  );
}

export async function adminCreatePremiumFrame(payload: {
  name: string;
  description: string;
  imageUrl: string;
  price: number;
  isActive: boolean;
}): Promise<{ ok: boolean; frame: any }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; frame: any }>(
      `/api/admin/premium/frames`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    )
  );
}

export async function adminUpdatePremiumFrame(id: string, payload: {
  name?: string;
  description?: string;
  imageUrl?: string;
  price?: number;
  isActive?: boolean;
}): Promise<{ ok: boolean; frame: any }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; frame: any }>(
      `/api/admin/premium/frames/${id}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    )
  );
}

export async function adminDeletePremiumFrame(id: string): Promise<{ ok: boolean; message?: string }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; message?: string }>(
      `/api/admin/premium/frames/${id}`,
      { method: "DELETE" }
    )
  );
}

// Premium Badges
export async function adminListPremiumBadges(): Promise<{ ok: boolean; badges: any[] }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; badges: any[] }>(
      `/api/admin/premium/badges`,
      { method: "GET" }
    )
  );
}

export async function adminCreatePremiumBadge(payload: {
  name: string;
  description: string;
  imageUrl: string;
  price: number;
  isActive: boolean;
}): Promise<{ ok: boolean; badge: any }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; badge: any }>(
      `/api/admin/premium/badges`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    )
  );
}

export async function adminUpdatePremiumBadge(id: string, payload: {
  name?: string;
  description?: string;
  imageUrl?: string;
  price?: number;
  isActive?: boolean;
}): Promise<{ ok: boolean; badge: any }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; badge: any }>(
      `/api/admin/premium/badges/${id}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    )
  );
}

export async function adminDeletePremiumBadge(id: string): Promise<{ ok: boolean; message?: string }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; message?: string }>(
      `/api/admin/premium/badges/${id}`,
      { method: "DELETE" }
    )
  );
}

// Premium Backgrounds
export async function adminListPremiumBackgrounds(): Promise<{ ok: boolean; backgrounds: any[] }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; backgrounds: any[] }>(
      `/api/admin/premium/backgrounds`,
      { method: "GET" }
    )
  );
}

export async function adminCreatePremiumBackground(payload: {
  name: string;
  description: string;
  imageUrl: string;
  price: number;
  isActive: boolean;
}): Promise<{ ok: boolean; background: any }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; background: any }>(
      `/api/admin/premium/backgrounds`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    )
  );
}

export async function adminUpdatePremiumBackground(id: string, payload: {
  name?: string;
  description?: string;
  imageUrl?: string;
  price?: number;
  isActive?: boolean;
}): Promise<{ ok: boolean; background: any }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; background: any }>(
      `/api/admin/premium/backgrounds/${id}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    )
  );
}

export async function adminDeletePremiumBackground(id: string): Promise<{ ok: boolean; message?: string }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; message?: string }>(
      `/api/admin/premium/backgrounds/${id}`,
      { method: "DELETE" }
    )
  );
}

// Admin announcement helper
export async function adminSendAnnouncement(params: {
  message: string;
  targetType: "all" | "active" | "verified";
}): Promise<{ ok: boolean; sent: number; message?: string }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; sent: number; message?: string }>(
      `/api/admin/announcements/send`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      }
    )
  );
}

/* ────────────────────────────────────────────────────────────
   🚀 FAZ 8: Post Boost + Comment Edit
───────────────────────────────────────────────────────────── */

// Post Boost (Admin)
export async function adminBoostPost(
  postId: string,
  body?: { durationMinutes?: number; multiplier?: number; expiresAt?: string; reason?: string }
): Promise<{ ok: boolean; post: any }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; post: any }>(
      `/api/admin/posts/${postId}/boost`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body || {}),
      }
    )
  );
}

// Remove Post Boost (Admin)
export async function adminRemoveBoost(postId: string): Promise<{ ok: boolean; post: any }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; post: any }>(
      `/api/admin/posts/${postId}/boost`,
      { method: "DELETE" }
    )
  );
}

// Admin Comment Edit (PUT /api/admin/comments/:id)
export async function adminEditCommentDirect(commentId: string, content: string): Promise<{ ok: boolean; comment: any }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: boolean; comment: any }>(
      `/api/admin/comments/${commentId}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: content }),
      }
    )
  );
}

/* ────────────────────────────────────────────────────────────
   📨 FAZ 9: Outbox Management
───────────────────────────────────────────────────────────── */

export type OutboxEventStatus = "queued" | "sending" | "delivered" | "failed";
export type OutboxChannel = "email" | "sse" | "push";
export type OutboxEventType = "email" | "sse" | "push" | "savwar_leaderboard" | "savlike_winner";

export interface OutboxEvent {
  _id: string;
  type: OutboxEventType;
  status: OutboxEventStatus;
  channel: OutboxChannel;
  payload: any;
  dedupKey?: string | null;
  attempts: number;
  maxAttempts: number;
  nextAttemptAt: string; // ISO date
  errorMessage?: string | null;
  lastError?: string | null;
  deliveredAt?: string | null;
  retryCount?: number;
  meta?: {
    userId?: string;
    postId?: string;
    notificationId?: string;
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
}

export interface OutboxListResponse {
  ok: true;
  events: OutboxEvent[];
  total: number;
  offset: number;
  limit: number;
}

export interface OutboxStatsResponse {
  ok: true;
  stats: {
    byStatus: {
      queued: number;
      sending: number;
      delivered: number;
      failed: number;
    };
    byChannel: {
      email: number;
      sse: number;
      push: number;
    };
    oldestQueuedAge: number | null;
  };
}

// List outbox events with filtering
export async function adminOutboxList(params?: {
  status?: OutboxEventStatus;
  channel?: OutboxChannel;
  type?: OutboxEventType;
  limit?: number;
  offset?: number;
}): Promise<OutboxListResponse> {
  const qs = buildQuery(params as any);
  return withAutoRefresh(() =>
    runWithAuth<OutboxListResponse>(`/api/admin/outbox${qs}`, { method: "GET" })
  );
}

// Get outbox statistics
export async function adminOutboxStats(): Promise<OutboxStatsResponse> {
  return withAutoRefresh(() =>
    runWithAuth<OutboxStatsResponse>(`/api/admin/outbox/stats`, { method: "GET" })
  );
}

// Retry a failed outbox event
export async function adminOutboxRetry(id: string): Promise<{ ok: true; message: string; event: any }> {
  return withAutoRefresh(() =>
    runWithAuth<{ ok: true; message: string; event: any }>(
      `/api/admin/outbox/${id}/retry`,
      { method: "POST" }
    )
  );
}

/* ────────────────────────────────────────────────────────────
   🛡️ FAZ 11: Security Dashboard API Functions
───────────────────────────────────────────────────────────── */

export async function adminGetSecurityStats(): Promise<any> {
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/security/stats`, { method: "GET" })
  );
}

export async function adminGetSecurityIpLog(params?: { limit?: number; offset?: number }): Promise<any> {
  const query = buildQuery(params as any);
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/security/ip-log${query}`, { method: "GET" })
  );
}

export async function adminGetSecuritySuspicious(params?: { minScore?: number; limit?: number }): Promise<any> {
  const query = buildQuery(params as any);
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/security/suspicious${query}`, { method: "GET" })
  );
}

export async function adminGetSecurityRateLimitLog(params?: { limit?: number; offset?: number }): Promise<any> {
  const query = buildQuery(params as any);
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/security/rate-limit-log${query}`, { method: "GET" })
  );
}

// ════════════════════════════════════════════════════════════
// FAZ 12: Login Attempts / Failed Logins API
// ════════════════════════════════════════════════════════════

export async function adminGetLoginAttempts(params?: { limit?: number; offset?: number }): Promise<any> {
  const query = buildQuery(params as any);
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/security/login-attempts${query}`, { method: "GET" })
  );
}

export async function adminGetFailedLogins(params?: { limit?: number; offset?: number }): Promise<any> {
  const query = buildQuery(params as any);
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/security/login-failures${query}`, { method: "GET" })
  );
}

export async function adminGetBlockedIps(params?: { limit?: number; offset?: number }): Promise<any> {
  const query = buildQuery(params as any);
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/security/login-blocks${query}`, { method: "GET" })
  );
}

export async function adminUnblockIp(ip: string): Promise<any> {
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/security/login-blocks/${encodeURIComponent(ip)}/unblock`, { method: "POST" })
  );
}

// ════════════════════════════════════════════════════════════
// FAZ 13: Notification Rules & System Alerts API
// ════════════════════════════════════════════════════════════

export async function adminNotificationListRules(): Promise<any> {
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/notifications/rules`, { method: "GET" })
  );
}

export async function adminNotificationCreateRule(rule: any): Promise<any> {
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/notifications/rules`, { method: "POST", body: JSON.stringify(rule) })
  );
}

export async function adminNotificationUpdateRule(id: string, rule: any): Promise<any> {
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/notifications/rules/${id}`, { method: "PATCH", body: JSON.stringify(rule) })
  );
}

export async function adminNotificationDeleteRule(id: string): Promise<any> {
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/notifications/rules/${id}`, { method: "DELETE" })
  );
}

export async function adminNotificationListSystemAlerts(params?: { limit?: number; offset?: number }): Promise<any> {
  const query = buildQuery(params as any);
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/notifications/system-alerts${query}`, { method: "GET" })
  );
}

export async function adminNotificationAcknowledgeAlert(id: string): Promise<any> {
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/notifications/system-alerts/${id}/ack`, { method: "PATCH" })
  );
}

export async function adminNotificationResolveAlert(id: string): Promise<any> {
  return withAutoRefresh(() =>
    runWithAuth<any>(`/api/admin/notifications/system-alerts/${id}/resolve`, { method: "PATCH" })
  );
}

// Toplu export (kullanımı kolaylaştırmak için)
export const Admin = {
  dashboardStats: adminDashboardStats,
  getReports: adminGetReports,
  reviewReport: adminReviewReport,
  getUsersWithPhone: adminGetUsersWithPhone,
  listAudit: adminListAudit,
  setRole: adminSetRole,
  suspend: adminSuspend,
  shadowban: adminShadowban,
  exemptDaily: adminExemptDaily,
  banUser: adminBanUser,
  unbanUser: adminUnbanUser,
  deleteUser: adminDeleteUser,
  restoreUser: adminRestoreUser,
  editPost: adminEditPost,
  deletePost: adminDeletePost,
  editComment: adminEditComment,
  deleteComment: adminDeleteComment,
  searchUsers: adminSearchUsers,
  securityOverview: adminSecurityOverview,
  securityAuditStats: adminSecurityAuditStats,
  securitySuspiciousIps: adminSecuritySuspiciousIps,
  securityRateLimitStats: adminSecurityRateLimitStats,
  securityMetrics: adminSecurityMetrics,
  sendAnnouncement: adminSendAnnouncement,
  // FAZ 8: Post Boost & Comment Edit
  boostPost: adminBoostPost,
  removeBoost: adminRemoveBoost,
  editCommentDirect: adminEditCommentDirect,
  // FAZ 9: Outbox Management
  outboxList: adminOutboxList,
  outboxStats: adminOutboxStats,
  outboxRetry: adminOutboxRetry,
  // FAZ 11: Security Dashboard
  getSecurityStats: adminGetSecurityStats,
  getSecurityIpLog: adminGetSecurityIpLog,
  getSecuritySuspicious: adminGetSecuritySuspicious,
  getSecurityRateLimitLog: adminGetSecurityRateLimitLog,
  // FAZ 12: Login Attempts / Failed Logins
  getLoginAttempts: adminGetLoginAttempts,
  getFailedLogins: adminGetFailedLogins,
  getBlockedIps: adminGetBlockedIps,
  unblockIp: adminUnblockIp,
  // FAZ 13: Notification Rules & System Alerts
  notificationListRules: adminNotificationListRules,
  notificationCreateRule: adminNotificationCreateRule,
  notificationUpdateRule: adminNotificationUpdateRule,
  notificationDeleteRule: adminNotificationDeleteRule,
  notificationListSystemAlerts: adminNotificationListSystemAlerts,
  notificationAcknowledgeAlert: adminNotificationAcknowledgeAlert,
  notificationResolveAlert: adminNotificationResolveAlert,
  // Premium Management
  premiumFrames: {
    list: adminListPremiumFrames,
    create: adminCreatePremiumFrame,
    update: adminUpdatePremiumFrame,
    delete: adminDeletePremiumFrame,
  },
  premiumBadges: {
    list: adminListPremiumBadges,
    create: adminCreatePremiumBadge,
    update: adminUpdatePremiumBadge,
    delete: adminDeletePremiumBadge,
  },
  premiumBackgrounds: {
    list: adminListPremiumBackgrounds,
    create: adminCreatePremiumBackground,
    update: adminUpdatePremiumBackground,
    delete: adminDeletePremiumBackground,
  },
};

// ────────────────────────────────────────────────────────────
// Köprü export'lar (eski import'ları kırmamak için)
// ────────────────────────────────────────────────────────────

export const Notifs = {
  listNotifications: fetchNotifications,
  markAllRead: markAllNotificationsRead,
  markRead: markNotificationRead,
};

/* ────────────────────────────────────────────────────────────
   🔐 Password reset helpers (YENİ)
───────────────────────────────────────────────────────────── */

export async function requestPasswordReset(email: string): Promise<{ ok: true }> {
  return apiPost<{ ok: true }>(`/api/auth/forgot`, { email });
}

export async function resetPassword(token: string, newPassword: string): Promise<{ ok: true; access?: string }> {
  return apiPost<{ ok: true; access?: string }>(`/api/auth/reset-password`, { token, newPassword });
}

/* ────────────────────────────────────────────────────────────
   🙂 Hata çevirisi (YENİ)
───────────────────────────────────────────────────────────── */
export function humanizeApiError(msg: string) {
  switch (msg) {
    case "email_taken": return "Bu e-posta zaten kayıtlı.";
    case "username_taken": return "Bu kullanıcı adı zaten alınmış.";
    case "invalid_or_expired_token": return "Bağlantı geçersiz veya süresi dolmuş.";
    case "invalid_credentials": return "Yanlış bir giriş, doğru bir düşünceye engel değildir.";
    case "no_refresh": return "Oturum bulunamadı.";
    case "api_error": return "Yanlış bir giriş, doğru bir düşünceye engel değildir.";
    default: return "Yanlış bir giriş, doğru bir düşünceye engel değildir.";
  }
}

/* ────────────────────────────────────────────────────────────
   OTP Authentication Functions (for backward compatibility)
───────────────────────────────────────────────────────────── */

/** Email-based OTP request */
export async function requestOtp(email: string): Promise<{ ok: true }> {
  return apiPost<{ ok: true }>("/api/auth/request-otp", { email });
}

/** Verify OTP code and get auth token */
export async function verifyOtp(
  email: string, 
  code: string
): Promise<{ ok: boolean; token?: string; user?: any }> {
  const response = await apiPost<{ 
    ok: boolean; 
    access?: string;
    token?: string; 
    user?: any 
  }>("/api/auth/verify-otp", { email, code });
  
  // Set token if received
  const token = response.access || response.token;
  if (token) setToken(token);
  
  return {
    ok: response.ok,
    token: token,
    user: response.user
  };
}

/* ────────────────────────────────────────────────────────────
   🏆 SavLike Leaderboard API
───────────────────────────────────────────────────────────── */

export type SavLikePeriod = 'daily' | 'weekly' | 'monthly' | 'yearly';

export type SavLikeEntry = {
  _id: string;
  postId: any;
  userId: any;
  period: SavLikePeriod;
  likes: number;
  comments: number;
  score: number;
  rank: number;
  delta: number;
  lastEventAt: string;
};

export type SavLikeLeaderboardResponse = {
  ok: true;
  period: SavLikePeriod;
  items: SavLikeEntry[];
  count: number;
};

export type SavLikeUserRankInfo = {
  rank: number;
  score: number;
  likes: number;
  comments: number;
  delta: number;
};

export type SavLikeMyRankResponse = {
  ok: true;
  userId: string;
  periods: {
    daily: SavLikeUserRankInfo | null;
    weekly: SavLikeUserRankInfo | null;
    monthly: SavLikeUserRankInfo | null;
    yearly: SavLikeUserRankInfo | null;
  };
};

/** Belirli bir periyot için SavLike liderlik tablosunu getir */
export async function getSavLikeLeaderboard(
  period: SavLikePeriod,
  limit: number = 50
): Promise<SavLikeLeaderboardResponse> {
  return apiGet<SavLikeLeaderboardResponse>(`/api/savlike/${period}?limit=${Math.min(limit, 100)}`);
}

/** Kullanıcının tüm periyotlardaki sıralama bilgisini getir */
export async function getMyCurrentRanks(): Promise<SavLikeMyRankResponse> {
  return apiGet<SavLikeMyRankResponse>(`/api/savlike/me/current`);
}

// ============================================================
// 🏛️ KABILE API
// ============================================================

export interface KabileMember {
  id: string;
  username: string;
  avatarUrl?: string;
  savwarTP: number;
  savwarRank: number;
  joinedAt: Date;
  role: 'leader' | 'member';
}

export interface PendingKabileRequest {
  id: string;
  userId: string;
  username: string;
  avatarUrl?: string;
  savwarTP: number;
  savwarRank: number;
  createdAt: Date;
}

export interface Kabile {
  id: string;
  name: string;
  description: string;
  flagUrl?: string;
  leaderId: string;
  maxMembers: number;
  minTP: number;
  createdAt: Date;
  members: KabileMember[];
  totalTP: number;
  isLeader: boolean;
  pendingRequests: PendingKabileRequest[];
}

export interface CreateKabileData {
  name: string;
  description: string;
  maxMembers: number;
  minTP: number;
}

/** Yeni kabile oluştur */
export async function createKabile(data: CreateKabileData): Promise<{ kabileId: string }> {
  return apiPost<{ kabileId: string }>("/api/kabile", data);
}

/** Kabile bayrağı yükle */
export async function uploadKabileFlag(kabileId: string, formData: FormData): Promise<{ flagUrl: string }> {
  return apiUpload<{ flagUrl: string }>(`/api/kabile/${kabileId}/flag`, formData);
}

/** Kullanıcının kabilesini getir */
export async function getMyKabile(): Promise<Kabile | null> {
  try {
    return await apiGet<Kabile>("/api/kabile/my");
  } catch (error: any) {
    if (error?.status === 404) {
      return null; // Kullanıcının kabilesi yok
    }
    throw error;
  }
}

/** Kabile ayarlarını güncelle (sadece lider) */
export async function updateKabile(
  kabileId: string,
  data: Partial<CreateKabileData>
): Promise<Kabile> {
  return apiPatch<Kabile>(`/api/kabile/${kabileId}`, data);
}

/** Kabileden ayrıl */
export async function leaveKabile(): Promise<void> {
  await apiDelete("/api/kabile/leave");
}

/** Kurucu rolünü devret (sadece kurucu) */
export async function transferFounder(kabileId: string, newFounderId: string): Promise<{ message: string; newFounderId: string }> {
  return apiPost(`/api/kabile/${kabileId}/transfer-founder`, { newFounderId });
}

/** Kabileyi sil (sadece lider) */
export async function deleteKabile(kabileId: string): Promise<void> {
  await apiDelete(`/api/kabile/${kabileId}`);
}

/** Üyeyi kabileden at (sadece lider) */
export async function kickKabileMember(kabileId: string, userId: string): Promise<void> {
  await apiDelete(`/api/kabile/${kabileId}/member/${userId}`);
}

/** [ADMIN] Global arena'dan kabile cevabını sil */
export async function adminDeleteKabileAnswer(kabileId: string, answerUserId: string): Promise<void> {
  await apiDelete(`/api/kabile/daily-battle/answer/${kabileId}/${answerUserId}`);
}

/** Tüm kabileleri listele (leaderboard için) */
export async function getAllKabileler(): Promise<Kabile[]> {
  return apiGet<Kabile[]>("/api/kabile/all");
}

/** Kullanıcıyı kabileye davet et (sadece lider) */
export async function inviteToKabile(kabileId: string, userId: string): Promise<void> {
  await apiPost(`/api/kabile/${kabileId}/invite/${userId}`, {});
}

/** Kabile davetini kabul et */
export async function acceptKabileInvite(notificationId: string): Promise<{ kabileId: string }> {
  return apiPost<{ kabileId: string }>(`/api/kabile/invite/${notificationId}/accept`, {});
}

/** Kabile davetini reddet */
export async function rejectKabileInvite(notificationId: string): Promise<void> {
  await apiPost(`/api/kabile/invite/${notificationId}/reject`, {});
}

/** Takip edilen kullanıcıları getir */
export async function getFollowing(): Promise<Array<{
  id: string;
  username: string;
  email?: string;
  bio?: string;
  avatarUrl?: string;
  avatarUrlSmall?: string;
  savwarTP?: number;
}>> {
  const response = await apiGet<{ ok: boolean; items: any[] }>(`/api/follow/following`);
  return response.items;
}

/** Kabile detaylarını ID ile getir */
export async function getKabileById(kabileId: string): Promise<any> {
  return await apiGet(`/api/kabile/${kabileId}`);
}

/** Kabileye katılma talebi gönder */
export async function requestJoinKabile(kabileId: string): Promise<void> {
  await apiPost(`/api/kabile/${kabileId}/join-request`, {});
}

/** Kabileye katılma talebini onayla (lider) */
export async function approveJoinRequest(notificationId: string): Promise<{ kabileId: string }> {
  const response = await apiPost<{ kabileId: string }>(`/api/kabile/join-request/${notificationId}/approve`, {});
  return response;
}

/** Kabileye katılma talebini reddet (lider) */
export async function rejectJoinRequest(notificationId: string): Promise<void> {
  await apiPost(`/api/kabile/join-request/${notificationId}/reject`, {});
}

